import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ACTION_LABELS, CHANGE_LABELS, SECTION_LABELS, labelFor, humanize } from './labels';
import './ProfileBuildPathReport.css';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export function eventLabel(e) {
  if (e.source === 'login') return 'Signed in';
  if (e.source === 'profile_change') return labelFor(CHANGE_LABELS, e.key);
  return labelFor(ACTION_LABELS, e.key);
}

export function eventDetail(e) {
  if (e.source === 'profile_change' && e.detail) return humanize(e.detail);
  if (e.source === 'login') return e.detail ? humanize(e.detail.replace(' / ', ' on ')) : null;
  return e.detail ? humanize(e.detail) : null;
}

/** "+3 days" style gap between two ISO timestamps (UTC, as the API sends them). */
export function formatGap(fromIso, toIso) {
  const mins = Math.round((new Date(toIso) - new Date(fromIso)) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return '';
  if (mins < 60) return `+${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `+${hours} hr`;
  const days = Math.round(hours / 24);
  return `+${days} ${days === 1 ? 'day' : 'days'}`;
}

function formatWhen(iso) {
  const d = new Date(`${iso}Z`);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** Admin tool: search an athlete, then read their story in order. */
export default function AthleteJourney() {
  const { API_URL } = useAuth();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_URL}/admin/reports/athlete-journey-search?q=${encodeURIComponent(q.trim())}`,
          { headers: authHeaders() },
        );
        if (!cancelled) setResults(res.data.athletes || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Search failed.');
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, API_URL]);

  const open = async (id) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/admin/reports/athlete-journey/${id}`,
        { headers: authHeaders() });
      setJourney(res.data);
      setResults([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load this athlete.');
    } finally {
      setLoading(false);
    }
  };

  const events = journey?.events || [];
  const last = events[events.length - 1];
  const sections = Object.entries(journey?.sections || {});
  const doneCount = sections.filter(([, done]) => done).length;

  return (
    <div className="journey">
      <div className="journey__search">
        <input
          type="search"
          aria-label="Search athletes"
          placeholder="Search an athlete by name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <p className="report-error">{error}</p>}
      {results.length > 0 && (
        <ul className="journey__results">
          {results.map((a) => (
            <li key={a.id}>
              <button type="button" onClick={() => open(a.id)}>
                <strong>{a.name}</strong> <span className="report-note">{a.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && <p className="report-loading">Loading…</p>}

      {journey && !loading && (
        <>
          <h4>{journey.athlete.name}</h4>
          <div className="journey__facts">
            <div className="journey__fact"><span>Joined</span><strong>{journey.athlete.joined_at ? formatWhen(journey.athlete.joined_at) : '—'}</strong></div>
            <div className="journey__fact"><span>Last signed in</span><strong>{journey.athlete.last_login_at ? formatWhen(journey.athlete.last_login_at) : 'Never'}</strong></div>
            <div className="journey__fact"><span>Sign-ins</span><strong>{journey.athlete.login_count ?? 0}</strong></div>
            <div className="journey__fact"><span>Sections done</span><strong>{sections.length ? `${doneCount} of ${sections.length}` : '—'}</strong></div>
          </div>
          <p className="report-note">
            Full detail covers the last {journey.raw_window_days} days. Before that, only the
            first time each action happened is kept.
          </p>

          <div className="journey__layout">
            <div>
              {events.length === 0 && <p className="report-note">No recorded activity for this athlete.</p>}
              <ol className="timeline">
                {events.map((e, i) => (
                  <li className="timeline__item" key={`${e.at}-${e.source}-${e.key}-${i}`}>
                    <div className="timeline__when">
                      {formatWhen(e.at)}
                      <small>{i > 0 ? formatGap(events[i - 1].at, e.at) : ''}</small>
                    </div>
                    <div className="timeline__rail">
                      <span className={`timeline__dot${e.is_first ? ' timeline__dot--first' : ''}${e.source === 'login' ? ' timeline__dot--login' : ''}`} />
                      {i < events.length - 1 && <span className="timeline__line" />}
                    </div>
                    <div className="timeline__body">
                      <div className="timeline__label">
                        {eventLabel(e)}
                        {e.is_first && <span className="timeline__badge">First time</span>}
                      </div>
                      {eventDetail(e) && <div className="timeline__detail">{eventDetail(e)}</div>}
                    </div>
                  </li>
                ))}
              </ol>
              {last && (
                <div className="journey__stop">
                  <strong>Last activity:</strong> {eventLabel(last)} on {formatWhen(last.at)}.
                </div>
              )}
            </div>
            <aside>
              <h5>Sections</h5>
              {sections.length === 0 && <p className="report-note">No completeness snapshot for this athlete yet.</p>}
              <ul className="journey__sections">
                {sections.map(([name, done]) => (
                  <li key={name}>
                    <span className={`journey__check${done ? ' journey__check--done' : ''}`}>{done ? '✓' : ''}</span>
                    {labelFor(SECTION_LABELS, name)}
                  </li>
                ))}
              </ul>
              {journey.sections_day && <p className="report-note">As of {journey.sections_day}.</p>}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
